<template>
    <main class="flex-1 relative overflow-y-auto py-6 focus:outline-none" tabindex="0">
        <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 px-5">
            <div class="md:flex md:items-center md:justify-between">
                <div class="flex-1 min-w-0">
                    <h1 class="py-0.5 text-2xl font-semibold text-gray-900">{{ $t('IOM') }}</h1>
                </div>
                <div class="mt-4 flex md:mt-0 md:ml-4">
                    <router-link
                        class="btn btn-blue shadow-sm rounded-md"
                        to="/dashboard/iom/new"
                    >
                        {{ $t('Create IOM') }}
                    </router-link>
                </div>
            </div>
        </div>
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div class="my-6 bg-white shadow overflow-hidden sm:rounded-md">
                <loading :status="loading"/>
                <div class="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200 sm:px-6">
                    <div class="flex-grow">
                        <label class="sr-only" for="search">{{ $t('Search') }}</label>
                        <div class="flex rounded-md shadow-sm">
                            <div class="relative flex-grow focus-within:z-10">
                                <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg-vue class="h-5 w-5 text-gray-400" icon="font-awesome.search-regular"></svg-vue>
                                </div>
                                <input
                                    id="search"
                                    v-model.lazy="filters.search"
                                    :placeholder="$t('Search number or subject')"
                                    class="form-input block w-full rounded-md pl-10 text-sm transition ease-in-out duration-150"
                                    @change="getIoms"
                                >
                            </div>
                        </div>
                    </div>
                </div>
                <template v-if="iomList.length > 0">
                    <ul>
                        <template v-for="(iom, index) in iomList">
                            <li :class="{'border-t border-gray-200': index !== 0}">
                                <router-link
                                    :to="'/dashboard/iom/' + iom.uuid"
                                    class="block hover:bg-gray-100 focus:outline-none focus:bg-gray-100 transition duration-150 ease-in-out"
                                >
                                    <div class="flex items-center px-4 py-4 sm:px-6">
                                        <div class="min-w-0 flex-1 flex items-center">
                                            <div class="min-w-0 flex-1 lg:grid lg:grid-cols-3 lg:gap-4">
                                                <div class="lg:col-span-2">
                                                    <div class="text-sm font-semibold leading-5 text-gray-900 truncate">
                                                        {{ iom.number }}
                                                    </div>
                                                    <div class="mt-1 text-sm font-medium leading-5 text-gray-800 truncate">
                                                        {{ iom.subject }}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div class="text-xs font-semibold text-gray-500 mb-1">{{ $t('To') }}</div>
                                                    <div class="flex flex-wrap gap-1">
                                                        <span
                                                            v-for="department in iom.departments"
                                                            :key="'iom-dept-' + iom.id + '-' + department.id"
                                                            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                                                        >
                                                            {{ department.name }}
                                                        </span>
                                                        <span
                                                            v-if="iom.departments.length === 0"
                                                            class="text-xs text-gray-400"
                                                        >
                                                            {{ $t('No divisions assigned') }}
                                                        </span>
                                                    </div>
                                                    <div class="mt-1 text-xs text-gray-500">{{ iom.memo_date_formatted }}</div>
                                                </div>
                                            </div>
                                        </div>
                                        <div>
                                            <svg-vue class="h-5 w-5 text-gray-400" icon="font-awesome.angle-right-regular"></svg-vue>
                                        </div>
                                    </div>
                                </router-link>
                            </li>
                        </template>
                    </ul>
                </template>
                <template v-else-if="!loading">
                    <div class="h-full flex">
                        <div class="m-auto">
                            <div class="grid grid-cols-1 justify-items-center h-full w-full px-4 py-10">
                                <div class="flex justify-center items-center">
                                    <svg-vue class="h-full h-auto w-64 mb-12" icon="undraw.browsing"></svg-vue>
                                </div>
                                <div class="flex justify-center items-center">
                                    <div class="w-full font-semibold text-2xl">{{ $t('No records found') }}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </template>
                <nav class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                    <div class="hidden sm:block">
                        <p class="text-sm leading-5 text-gray-700">
                            {{ $t('Showing') }}
                            <span class="font-medium">{{ paginationTotalStart }}</span>
                            {{ $t('to') }}
                            <span class="font-medium">{{ paginationTotalEnd }}</span>
                            {{ $t('of') }}
                            <span class="font-medium">{{ pagination.total }}</span>
                            {{ $t('results') }}
                        </p>
                    </div>
                    <div class="flex-1 flex justify-between sm:justify-end">
                        <button
                            :class="pagination.currentPage <= 1 ? 'opacity-50 cursor-not-allowed' : ''"
                            :disabled="pagination.currentPage <= 1"
                            class="pagination-link"
                            type="button"
                            @click="changePage(pagination.currentPage - 1)"
                        >
                            {{ $t('Previous') }}
                        </button>
                        <button
                            :class="pagination.currentPage >= pagination.totalPages ? 'opacity-50 cursor-not-allowed' : ''"
                            :disabled="pagination.currentPage >= pagination.totalPages"
                            class="ml-3 pagination-link"
                            type="button"
                            @click="changePage(pagination.currentPage + 1)"
                        >
                            {{ $t('Next') }}
                        </button>
                    </div>
                </nav>
            </div>
        </div>
    </main>
</template>

<script>
export default {
    name: "iom-list",
    metaInfo() {
        return {
            title: this.$i18n.t('IOM')
        }
    },
    data() {
        return {
            page: 1,
            filters: {
                search: null,
            },
            perPage: 10,
            loading: true,
            pagination: {
                currentPage: 0,
                perPage: 0,
                total: 0,
                totalPages: 0
            },
            iomList: [],
        }
    },
    computed: {
        paginationTotalStart() {
            return (this.pagination.perPage * this.pagination.currentPage) - this.pagination.perPage + 1;
        },
        paginationTotalEnd() {
            return this.pagination.perPage * this.pagination.currentPage <= this.pagination.total
                ? this.pagination.perPage * this.pagination.currentPage
                : this.pagination.total;
        },
    },
    mounted() {
        this.getIoms();
    },
    methods: {
        changePage(page) {
            const self = this;
            if ((page > 0) && (page <= self.pagination.totalPages) && (page !== self.page)) {
                self.page = page;
                self.getIoms();
            }
        },
        getIoms() {
            const self = this;
            self.loading = true;
            axios.get('api/dashboard/iom', {
                params: {
                    page: self.page,
                    perPage: self.perPage,
                    search: self.filters.search
                }
            }).then(function (response) {
                self.iomList = response.data.items;
                self.pagination = response.data.pagination;
                if (self.pagination.totalPages < self.pagination.currentPage) {
                    self.page = self.pagination.totalPages;
                    self.getIoms();
                } else {
                    self.loading = false;
                }
            }).catch(function () {
                self.loading = false;
            });
        },
    }
}
</script>
